import React, { useState, useEffect } from 'react';
import { Team } from '../types/Team';
import { TeamMetadata } from './TeamMetadata';
import { RaceDetails } from './RaceDetails';
import { fetchTeamById } from '../api/apis';

interface TeamDetailsProps {
  teamId: string;
}

export const TeamDetails = ({ teamId }: TeamDetailsProps) => {
  const [team, setTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTeam = async () => {
    setLoading(true);
    setError(null);
    try {
      const teamData = await fetchTeamById(teamId);
      setTeam(teamData);
    } catch (err) {
      setError('Failed to load team details');
      console.error('Error loading team:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTeam();
  }, [teamId]);

  if (loading) {
    return <div className="bg-white rounded-lg shadow p-6">Loading team details...</div>;
  }

  if (error || !team) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error || 'Team not found'}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-800 mb-4">{team.name}</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Category</h3>
            <p className="mt-1 text-lg text-gray-900">{team.category}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Heat</h3>
            <p className="mt-1 text-lg text-gray-900">{team.startHeat}</p>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
        <div className="text-sm font-medium text-gray-500">Team Members</div>
        <div className="grid grid-cols-2 gap-2">
          {team.members.map((member, index) => (
            <div key={index} className="bg-white rounded-md p-2 text-gray-800">
              {member.name}
            </div>
          ))}
        </div>
      </div>
      </div>

      <RaceDetails team={team} onTeamUpdate={loadTeam} />
    </div>
  );
};

const LoadingState = ({ message }: { message: string }) => (
  <div className="max-w-4xl mx-auto p-6 flex justify-center items-center">
    <div className="text-gray-600">{message}</div>
  </div>
);

const ErrorState = ({ message }: { message: string }) => (
  <div className="max-w-4xl mx-auto p-6 flex justify-center items-center">
    <div className="text-red-600">{message}</div>
  </div>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="max-w-4xl mx-auto p-6 flex justify-center items-center">
    <div className="text-gray-600">{message}</div>
  </div>
);